import React, { useEffect, useState } from 'react';
import { Typography, Breadcrumb, Card, Button, Space, Tag, Modal, Select, message, List, Avatar, Switch, Popconfirm, Form, Input } from 'antd';
import { useNavigate } from 'react-router-dom';
import { authHeader } from '../../../utils/api';
import { DeleteOutlined } from '@ant-design/icons';

const { Title, Paragraph } = Typography;

const AllClientsList = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSaving, setAssignSaving] = useState(false);
  const [selectedAgencyId, setSelectedAgencyId] = useState(null);
  const [activeMap, setActiveMap] = useState({});
  const [linkTargetClient, setLinkTargetClient] = useState(null);
  const navigate = useNavigate();
  // New Client modal
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createSaving, setCreateSaving] = useState(false);
  const [geotabDatabases, setGeotabDatabases] = useState([]);
  const [loadingGeotabDbs, setLoadingGeotabDbs] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        setLoading(true);
        const res = await fetch(`/api/users?limit=5000&offset=0`);
        if (!res.ok) {
          console.warn('Clients fetch failed', res.status, res.statusText);
          if (mounted) setData([]);
          return;
        }
        const json = await res.json();
        const items = Array.isArray(json.items) ? json.items : [];
        
        // Filter only clients (role = 'client')
        const clientsData = items.filter(user => user.role === 'client');
        
        const mapped = clientsData.map((row) => ({
          id: row.id,
          name: row.full_name || `Client #${row.id}`,
          user_id: row.id, // For compatibility
          email: row.email || '',
          agency: '', // Clients don't have agency association in this view
          status: 'active', // Default for now
          is_active: true,
          created_at: row.created_at ? new Date(row.created_at).toLocaleDateString() : '',
          geotab_database: '' // Will be populated by Geotab API call
        }));
        if (mounted) setData(mapped);
        
        // Initialize active map from backend data
        const activeMapInit = {};
        items.forEach(client => {
          activeMapInit[client.id] = client.is_active !== false; // default to true if undefined
        });
        setActiveMap(activeMapInit);
      } catch (e) {
        console.error('Failed to load clients:', e);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    const loadAgencies = async () => {
      try {
        const res = await fetch('/api/users?limit=5000&offset=0');
        if (res.ok && mounted) {
          const json = await res.json();
          const items = Array.isArray(json.items) ? json.items : [];

          // Filter only enterprises (role = 'agency')
          const enterprises = items.filter(user => user.role === 'agency');

          // Map to the expected format for the dropdown
          const mappedEnterprises = enterprises.map(enterprise => ({
            id: enterprise.id,
            name: enterprise.full_name || enterprise.email || `Enterprise #${enterprise.id}`
          }));

          setAgencies(mappedEnterprises);
        }
      } catch (e) {
        console.error('Failed to load agencies:', e);
      }
    };
    load();
    loadAgencies();
    loadGeotabDatabases();
    return () => { mounted = false; };
  }, []);

  const loadGeotabDatabases = async () => {
    try {
      setLoadingGeotabDbs(true);
      const res = await fetch('/api/integrations/geotab/accounts', { headers: authHeader() });
      if (res.ok) {
        const json = await res.json();
        const items = Array.isArray(json) ? json : (Array.isArray(json?.items) ? json.items : []);
        const databases = items
          .filter(a => a?.database)
          .map(a => ({
            value: a.database,
            label: `${a.name || a.database} (${a.database})`,
            account: a
          }));
        setGeotabDatabases(databases);
        if (databases.length === 0) {
          message.info('No Geotab databases found. Add accounts in Integrations > GeoTab first.');
        }
      } else {
        message.error(`Failed to load Geotab databases (Status: ${res.status})`);
      }
    } catch (e) {
      console.error('Failed to load Geotab databases:', e);
      message.error('Failed to load Geotab databases: ' + e.message);
    } finally {
      setLoadingGeotabDbs(false);
    }
  };

  const openAssign = (client) => {
    setSelectedAgencyId(null);
    setLinkTargetClient(client);
    setAssignOpen(true);
  };

  const openCreateModal = () => {
    form.resetFields();
    setCreateModalOpen(true);
  };

  const handleCreateClient = async () => {
    try {
      const values = await form.validateFields();
      setCreateSaving(true);
      
      const payload = {
        name: values.name,
        email: values.email,
        geotab_database: values.geotab_database || null,
        is_active: true,
        status: 'active'
      };
      
      const res = await fetch('/api/clients', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader()
        },
        body: JSON.stringify(payload)
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to create client: ${res.status}`);
      }
      
      await res.json();
      message.success(`Client "${values.name}" created successfully`);
      
      // Refresh the list
      const listRes = await fetch(`/api/clients?limit=5000&offset=0`);
      if (listRes.ok) {
        const json = await listRes.json();
        const items = Array.isArray(json.items) ? json.items : [];
        const mapped = items.map((row) => ({
          id: row.id,
          name: row.name || row.user_full_name || `Client #${row.id}`,
          user_id: row.user_id ?? null,
          email: row.user_email || '',
          agency: row.agency_id != null ? `Agency #${row.agency_id}` : '',
          status: row.status || (row.is_active === false ? 'inactive' : 'active'),
          is_active: row.is_active !== false,
          created_at: row.created_at ? new Date(row.created_at).toLocaleDateString() : '',
          geotab_database: row.geotab_database || ''
        }));
        setData(mapped);
        
        const activeMapInit = {};
        items.forEach(client => {
          activeMapInit[client.id] = client.is_active !== false;
        });
        setActiveMap(activeMapInit);
      }
      
      setCreateModalOpen(false);
      form.resetFields();
    } catch (e) {
      if (e?.errorFields) return; // form validation error
      message.error(e.message || 'Failed to create client');
    } finally {
      setCreateSaving(false);
    }
  };


  const handleToggleActive = async (id, val) => {
    try {
      console.log(`Updating client ${id} to ${val ? 'active' : 'inactive'}`);
      
      const requestBody = { 
        status: val ? 'active' : 'inactive',
        is_active: val
      };
      console.log('Request body:', requestBody);
      
      const response = await fetch(`/api/clients/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader()
        },
        body: JSON.stringify(requestBody)
      });

      console.log('Response status:', response.status);
      
      if (response.ok) {
        const responseData = await response.json();
        console.log('Response data:', responseData);
        
        setActiveMap(prev => ({ ...prev, [id]: val }));
        message.success(`Client #${id} ${val ? 'activated' : 'deactivated'}`);
      } else {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to update client status: ${response.status}`);
      }
    } catch (error) {
      message.error('Failed to update client status');
      console.error('Error updating client status:', error);
    }
  };

  const handleAssign = async () => {
    if (!linkTargetClient || !selectedAgencyId) {
      message.warning('Select an agency');
      return;
    }
    setAssignSaving(true);
    try {
      const res = await fetch(`/api/clients/${linkTargetClient.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agency_id: Number(selectedAgencyId) }),
      });
      if (!res.ok) throw new Error(`Assign failed: ${res.status} ${res.statusText}`);
      message.success('Client assigned to agency');
      // Update local table
      setData(prev => prev.map(row => row.id === linkTargetClient.id
        ? { ...row, agency: `Agency #${selectedAgencyId}` }
        : row
      ));
      setAssignOpen(false);
    } catch (e) {
      message.error(e.message || 'Failed to assign');
    } finally {
      setAssignSaving(false);
    }
  };

  const handleDelete = async (clientId) => {
    try {
      const res = await fetch(`/api/clients/${clientId}`, {
        method: 'DELETE',
        headers: authHeader()
      });
      if (!res.ok) {
        throw new Error(`Delete failed: ${res.status} ${res.statusText}`);
      }
      message.success(`Client #${clientId} deleted successfully`);
      // Remove from local state
      setData(prev => prev.filter(client => client.id !== clientId));
    } catch (e) {
      message.error(e.message || 'Failed to delete client');
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[
        { title: 'Admin' },
        { title: 'Clients' },
        { title: 'All Clients' },
      ]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>All Clients</Title>
        <Space>
          <Button type="primary" onClick={openCreateModal}>New Client</Button>
        </Space>
      </div>

      <Card>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Loaded from <code>/api/clients</code>. Toggle states are saved locally until backend fields are added.
        </Paragraph>
        <List
          grid={{ gutter: 16, column: 3 }}
          loading={loading}
          dataSource={data}
          rowKey={(r) => r.id}
          renderItem={(client) => (
            <List.Item key={client.id}>
              <Card hoverable style={{ borderRadius: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Avatar style={{ backgroundColor: '#6c63ff' }}>{String(client.name || 'C').trim()[0]?.toUpperCase() || 'C'}</Avatar>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <strong style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{client.name || `Client #${client.id}`}</strong>
                      <Tag color="green">Verified</Tag>
                    </div>
                    <div style={{ color: '#6b7280', fontSize: 12 }}>{client.email || '—'}</div>
                    {client.geotab_database && (
                      <div style={{ color: '#6366f1', fontSize: 11, marginTop: 2 }}>📊 Geotab: {client.geotab_database}</div>
                    )}
                  </div>
                </div>

                <div style={{ marginTop: 12 }}>
                  <Tag style={{ marginBottom: 8 }}>Individual</Tag>
                  <div style={{ background: '#f8fafc', borderRadius: 8, padding: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ 
                          width: 8, 
                          height: 8, 
                          background: activeMap[client.id] ? '#16a34a' : '#ef4444', 
                          borderRadius: 999 
                        }} />
                        <span>{activeMap[client.id] ? 'Active' : 'Inactive'}</span>
                      </div>
                      <Switch
                        checked={!!activeMap[client.id]}
                        onChange={(v) => handleToggleActive(client.id, v)}
                      />
                    </div>
                  </div>
                  <div style={{ color: '#6b7280', fontSize: 12, marginTop: 8 }}>Created: {client.created_at || '—'}</div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                  <Space size={8}>
                    <Button size="small" onClick={() => navigate(`/admin/clients/details?id=${client.id}`)}>View</Button>
                    <Button size="small" type="primary" onClick={() => navigate(`/admin/clients/details?id=${client.id}`)}>Edit</Button>
                  </Space>
                  <Space size={8}>
                    <Button size="small" onClick={() => openAssign(client)}>Assign/Transfer</Button>
                    <Popconfirm
                      title="Delete this client?"
                      description="This action cannot be undone."
                      okText="Delete"
                      cancelText="Cancel"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => handleDelete(client.id)}
                    >
                      <Button size="small" danger icon={<DeleteOutlined />}>Delete</Button>
                    </Popconfirm>
                  </Space>
                </div>
              </Card>
            </List.Item>
          )}
        />
      </Card>

      <Modal
        open={assignOpen}
        title={linkTargetClient ? `Assign/Transfer Client #${linkTargetClient.id}` : 'Assign/Transfer Client'}
        okText="Assign"
        onOk={handleAssign}
        okButtonProps={{ loading: assignSaving }}
        onCancel={() => setAssignOpen(false)}
      >
        <div style={{ marginTop: 8 }}>
          <div style={{ marginBottom: 8 }}>Select Agency</div>
          <Select
            style={{ width: '100%' }}
            placeholder="Choose agency"
            value={selectedAgencyId}
            onChange={setSelectedAgencyId}
            showSearch
            optionFilterProp="label"
            options={agencies.map(a => ({ value: a.id, label: a.name }))}
          />
        </div>
      </Modal>

      <Modal
        open={createModalOpen}
        title="Create New Client"
        okText="Create Client"
        onOk={handleCreateClient}
        okButtonProps={{ loading: createSaving }}
        onCancel={() => {
          setCreateModalOpen(false);
          form.resetFields();
        }}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 16 }}
        >
          <Form.Item
            label="Client Name"
            name="name"
            rules={[{ required: true, message: 'Please enter client name' }]}
          >
            <Input placeholder="Enter client name" />
          </Form.Item>
          
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input placeholder="client@example.com" />
          </Form.Item>
          
          <Form.Item
            label={(
              <span>
                Geotab Database
                <Button 
                  type="link" 
                  size="small" 
                  onClick={loadGeotabDatabases}
                  loading={loadingGeotabDbs}
                  style={{ marginLeft: 8, padding: 0, height: 'auto' }}
                >
                  {loadingGeotabDbs ? 'Loading...' : 'Refresh'}
                </Button>
              </span>
            )}
            name="geotab_database"
            extra={geotabDatabases.length === 0 ? "No databases found. Click 'Refresh' or add accounts in Integrations > GeoTab" : "Select the Geotab database this client will use for GPS data when building campaigns"}
          >
            <Select
              placeholder={loadingGeotabDbs ? "Loading databases..." : "Select Geotab database (optional)"}
              allowClear
              options={geotabDatabases}
              showSearch
              loading={loadingGeotabDbs}
              notFoundContent={loadingGeotabDbs ? 'Loading...' : 'No databases found'}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AllClientsList;
